// Payment note / payment request — React port of payments/PaymentViewComponent.vue.

import { formatDate, hasTranslation, t } from '../../i18n';
import { AttachmentLinks, DocumentHeader, ExtraAttributesTable, PartyBlock } from './OrderDocument';
import { asRecord, gatewayLabel, makeFormatters, str, toNumber, type AnyRecord } from './format';
import { useStoreSettings, type StoreSettings } from './storeSettings';

export function PaymentDocument({ record, settings: settingsOverride }: { record: AnyRecord; settings?: StoreSettings }) {
  const loaded = useStoreSettings();
  const settings = settingsOverride ?? loaded;
  const fmt = makeFormatters(settings);
  const received = toNumber(record.received) === 1;
  const user = asRecord(record.user);
  const account = asRecord(record.account);
  const fromCustomer = str(record.payable_type).includes('Customer');

  return (
    <div className="vd-doc">
      <DocumentHeader record={record} settings={settings} reference={record.reference} />
      <h3 className="vd-heading">{received ? t('doc.paymentNote') : t('doc.paymentRequest')}</h3>
      <div className="vd-row">
        <div>
          {t('doc.date')}: <strong>{formatDate(record.created_at ?? record.date)}</strong>
          {user ? (
            <>
              <br />
              {t('doc.createdBy')}: <strong>{str(user.name)}</strong>
            </>
          ) : null}
        </div>
        <div>
          {t('doc.reference')}: <strong>{str(record.reference)}</strong>
        </div>
      </div>
      <div className="vd-parties">
        <PartyBlock title={t('doc.location')} party={asRecord(record.location)} />
        <PartyBlock title={fromCustomer ? t('doc.from') : t('doc.to')} party={asRecord(record.payable)} />
      </div>
      <table className="vd-table">
        <tbody>
          {account ? (
            <tr>
              <td>{t('doc.account')}</td>
              <td>{str(account.name)}</td>
            </tr>
          ) : null}
          <tr>
            <td>{t('doc.gateway')}</td>
            <td>{gatewayLabel(record.gateway, t, hasTranslation)}</td>
          </tr>
          {record.cheque_number ? (
            <tr>
              <td>{t('doc.chequeNumber')}</td>
              <td>{str(record.cheque_number)}</td>
            </tr>
          ) : null}
          {record.gift_card_number ? (
            <tr>
              <td>{t('doc.giftCardNumber')}</td>
              <td>{str(record.gift_card_number)}</td>
            </tr>
          ) : null}
          <tr>
            <td className="vd-bold">{received ? t('doc.amountReceived') : t('doc.amountRequested')}</td>
            <td className="vd-bold">{fmt.money(record.amount)}</td>
          </tr>
        </tbody>
      </table>
      <ExtraAttributesTable record={record} />
      <AttachmentLinks record={record} />
      {record.details ? <div className="vd-box">{str(record.details)}</div> : null}
      <p className="vd-cgd">{t('doc.cgd')}</p>
    </div>
  );
}
